/*
 * The room's fixed lists and colours. Module tones are mixed here in JS (the
 * dashboard's browsers do not all have CSS color-mix()).
 */

/**
 * The analyses a sitting is read with, in the order a reviewer thinks of them, and the feed kinds each holds.
 * `setting` names the ProctorSettings flag that turns it on (overall is always on).
 */
export const ANALYSES = [
  { k: "image", n: "Image analysis", short: "Image", setting: "image", d: "Camera captures checked for another person, looking away, and a phone or another device in view; the profile check", kinds: ["capture", "profile"] },
  { k: "audio", n: "Audio analysis", short: "Audio", setting: "audio", d: "Speech heard outside the answers, and moments the audio review flagged", kinds: ["audio", "quote"] },
  { k: "screen", n: "Screen analysis", short: "Screen", setting: "screen", d: "Screen snapshots checked for something that should not be on screen", kinds: ["screen"] },
  { k: "ai", n: "AI-use analysis", short: "AI use", setting: "ai", d: "Answers that may be AI-written or read aloud (experimental)", kinds: ["integrity", "typing", "passage"] },
  { k: "overall", n: "Browser activity", short: "Browser", setting: null, d: "Tab switches, counted per question, and session restarts", kinds: ["session", "tabcount", "tab", "fullscreen", "clipboard"] },
];

/** The analysis a feed kind belongs to. */
export const analysisOf = (kind) => (ANALYSES.find((a) => a.kinds.includes(kind)) || ANALYSES[ANALYSES.length - 1]).k;
/** Its short name. */
export const analysisName = (kind) => ANALYSES.find((a) => a.k === analysisOf(kind)).short;

export const SPEEDS = [1, 1.5, 2];

/**
 * How long after its moment an item stays lit on the player and in the list. A whole answer (integrity) or a
 * question's tab count spans minutes, so it is lit only around its start.
 */
export const liveSpan = (x) => (x.kind === "integrity" || x.kind === "tabcount" ? 0 : x.dur || 0);
export const SEV_RANK = { critical: 0, review: 1, clear: 2 };

/** Question types whose answer is spoken, so speech there is expected. */
export const SPOKEN_TYPES = ["speech", "spoken", "speech_scripted", "speech_spontaneous", "audio", "voice"];
export const isSpokenQuestion = (q) => !!q && SPOKEN_TYPES.includes(String(q.type || "").toLowerCase());

export const MODULE_TONES = ["#6366F1", "#0EA5E9", "#14B8A6", "#F59E0B", "#EC4899", "#8B5CF6"];

const hex = (h) => {
  const x = h.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(x.slice(i, i + 2), 16));
};
const toHex = (rgb) => `#${rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("")}`;

/** `a` mixed with `b`, `p` of the way to `a` (as color-mix(in srgb, a p, b)). */
export const mix = (a, b, p) => {
  const [x, y] = [hex(a), hex(b)];
  return toHex(x.map((v, i) => v * p + y[i] * (1 - p)));
};

/**
 * A module's colours: the tone, a soft fill and ink for its label on light, and a muted bar on the dark player.
 * @param {number} i the module's index
 */
export function toneOf(i) {
  const tone = MODULE_TONES[((i % MODULE_TONES.length) + MODULE_TONES.length) % MODULE_TONES.length];
  return {
    tone,
    soft: mix(tone, "#ffffff", 0.16),
    softHover: mix(tone, "#ffffff", 0.26),
    ink: mix(tone, "#000000", 0.8),
    bar: mix(tone, "#2a2d35", 0.55),
    pill: mix(tone, "#0f1115", 0.7),
  };
}

/** The line icons for each analysis (24 grid, 1.75 stroke, round ends). */
export const KIND_PATHS = {
  image: ["M3 7V5a2 2 0 0 1 2-2h2", "M17 3h2a2 2 0 0 1 2 2v2", "M21 17v2a2 2 0 0 1-2 2h-2", "M7 21H5a2 2 0 0 1-2-2v-2", "M8 14s1.5 2 4 2 4-2 4-2", "M9 9h.01", "M15 9h.01"],
  audio: ["M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z", "M19 10v2a7 7 0 0 1-14 0v-2", "M12 19v3"],
  screen: ["M4 3h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z", "M8 21h8", "M12 17v4"],
  ai: ["M9.94 15.5A2 2 0 0 0 8.5 14.06l-6.13-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.13a.5.5 0 0 1 .96 0l1.58 6.13a2 2 0 0 0 1.44 1.44l6.13 1.58a.5.5 0 0 1 0 .96l-6.13 1.58a2 2 0 0 0-1.44 1.44l-1.58 6.13a.5.5 0 0 1-.96 0z", "M20 3v4", "M22 5h-4"],
  overall: ["M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z", "M2 9h20", "M6 6.5h.01", "M9 6.5h.01"],
  quote: ["M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z", "M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"],
  profile: ["M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2", "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "M16 11l2 2 4-4"],
};

/** The icon for a feed item: quotes and the profile check have their own, the rest their analysis's. */
export const iconOf = (kind) => (kind === "quote" ? "quote" : kind === "profile" ? "profile" : analysisOf(kind));
